import React, { useEffect, useMemo, useState } from 'react';
import './AddForm.css';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import EntityPickerInput, { EntityPickerOption } from './EntityPickerInput';
import VoluntaryEntityCreateModal, { VoluntaryEntityType } from './VoluntaryEntityCreateModal';
import { getApiV1Url, getVoluntaryCertificateItemUrl } from '../config/api';

type Manufacturer = { id: number; name: string; inn: string };
type Laboratory = { id: number; name: string; number: string; status: string };
type OsBody = { id: number; name: string; code: string; status: string };

type CertificateFormState = {
    apts_comment: string;
    certificate_link: string;
    certificate_number: string;
    certificate_validity_period: string;
    sds_name: string;
    blank_number: string;
    certificate_registration_date: string;
    certificate_expiry_date: string;
    regulatory_document: string;
    product: string;
    comment: string;
    number_appeal: string;
    manufacturer: string;
    laboratory: string;
    os: string;
};

type EntityModalState = {
    type: VoluntaryEntityType;
    prefill: string;
};

const emptyForm = (): CertificateFormState => ({
    apts_comment: '',
    certificate_link: '',
    certificate_number: '',
    certificate_validity_period: '',
    sds_name: '',
    blank_number: '',
    certificate_registration_date: '',
    certificate_expiry_date: '',
    regulatory_document: '',
    product: '',
    comment: '',
    number_appeal: '',
    manufacturer: '',
    laboratory: '',
    os: '',
});

const normalizeText = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    return String(value).trim().replace(/\s+/g, ' ');
};

const buildManufacturerLabel = (item: Pick<Manufacturer, 'name'>) => normalizeText(item.name);

const buildLaboratoryLabel = (item: Pick<Laboratory, 'name'>) => normalizeText(item.name);

const buildOsLabel = (item: Pick<OsBody, 'name'>) => normalizeText(item.name);

const authHeaders = () => {
    const access = localStorage.getItem('access');
    return {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(access ? { Authorization: `Bearer ${access}` } : {}),
    };
};

const compactPayload = (payload: Record<string, unknown>) => {
    const result: Record<string, unknown> = {};
    Object.entries(payload).forEach(([key, value]) => {
        if (value === '' || value === undefined || value === null) return;
        result[key] = value;
    });
    return result;
};

export default function VoluntaryAddFormPage() {
    const navigate = useNavigate();
    const { id: idParam } = useParams<{ id?: string }>();
    const certificateId = idParam ? Number(idParam) : null;
    const isEdit = Boolean(certificateId && !Number.isNaN(certificateId));

    const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
    const [laboratories, setLaboratories] = useState<Laboratory[]>([]);
    const [osBodies, setOsBodies] = useState<OsBody[]>([]);
    const [certSource, setCertSource] = useState<any[]>([]);
    const [form, setForm] = useState<CertificateFormState>(emptyForm);
    const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | null>(null);
    const [selectedLaboratoryId, setSelectedLaboratoryId] = useState<number | null>(null);
    const [selectedOsId, setSelectedOsId] = useState<number | null>(null);
    const [entityModal, setEntityModal] = useState<EntityModalState | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(isEdit);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchRefs = async () => {
            try {
                const headers = authHeaders();
                const [mans, labs, os, certs] = await Promise.all([
                    axios.get(getApiV1Url('VOLUNTARY_CERTIFICATES_MANUFACTURE'), { headers }),
                    axios.get(getApiV1Url('VOLUNTARY_CERTIFICATES_LABORATORY'), { headers }).catch(() => ({ data: [] })),
                    axios.get(getApiV1Url('VOLUNTARY_CERTIFICATES_OS'), { headers }).catch(() => ({ data: [] })),
                    axios.get(getApiV1Url('VOLUNTARY_CERTIFICATES'), { headers, timeout: 30000 }).catch(() => ({ data: [] })),
                ]);
                setManufacturers(Array.isArray(mans.data)
                    ? mans.data.map((m: any) => ({ id: m.id, name: m.manufacturer || '', inn: m.inn_unp || '' }))
                    : []);
                setLaboratories(Array.isArray(labs.data)
                    ? labs.data.map((l: any) => ({
                        id: l.id,
                        name: l.laboratory_name || l.name || '',
                        number: l.accreditation_certificate || l.number || '',
                        status: l.laboratory_ral_status || '',
                    }))
                    : []);
                setOsBodies(Array.isArray(os.data)
                    ? os.data.map((o: any) => ({
                        id: o.id,
                        name: o.os_name || o.name || '',
                        code: o.os_code || o.code || '',
                        status: o.os_ral_status || o.status || '',
                    }))
                    : []);
                setCertSource(Array.isArray(certs.data) ? certs.data : []);
            } catch {
                setManufacturers([]);
                setLaboratories([]);
                setOsBodies([]);
            }
        };
        fetchRefs();
    }, []);

    useEffect(() => {
        if (!isEdit || !certificateId) return;

        let cancelled = false;
        const loadCertificate = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(getVoluntaryCertificateItemUrl(certificateId), {
                    headers: authHeaders(),
                });
                if (cancelled) return;
                const data = response.data;
                setForm({
                    apts_comment: data.apts_comment || '',
                    certificate_link: data.certificate_link || '',
                    certificate_number: data.certificate_number || '',
                    certificate_validity_period: data.certificate_validity_period_manual || '',
                    sds_name: data.sds_name || '',
                    blank_number: data.form_number || '',
                    certificate_registration_date: data.certificate_registration_date || '',
                    certificate_expiry_date: data.certificate_expiry_date || '',
                    regulatory_document: data.regulatory_document || '',
                    product: data.product || '',
                    comment: data.comment || '',
                    number_appeal: data.appeal_number || '',
                    manufacturer: data.manufacturer || '',
                    laboratory: data.laboratory_name || '',
                    os: data.os_name || '',
                });
            } catch (err: any) {
                if (!cancelled) {
                    const serverData = err?.response?.data;
                    setError(serverData ? JSON.stringify(serverData) : 'Не удалось загрузить сертификат');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadCertificate();
        return () => { cancelled = true; };
    }, [certificateId, isEdit]);

    useEffect(() => {
        if (!form.manufacturer) {
            setSelectedManufacturerId(null);
            return;
        }
        const found = manufacturers.find((item) => buildManufacturerLabel(item) === normalizeText(form.manufacturer));
        setSelectedManufacturerId(found?.id ?? null);
    }, [form.manufacturer, manufacturers]);

    useEffect(() => {
        if (!form.laboratory) {
            setSelectedLaboratoryId(null);
            return;
        }
        const found = laboratories.find((item) => buildLaboratoryLabel(item) === normalizeText(form.laboratory));
        setSelectedLaboratoryId(found?.id ?? null);
    }, [form.laboratory, laboratories]);

    useEffect(() => {
        if (!form.os) {
            setSelectedOsId(null);
            return;
        }
        const found = osBodies.find((item) => buildOsLabel(item) === normalizeText(form.os));
        setSelectedOsId(found?.id ?? null);
    }, [form.os, osBodies]);

    const sdsOptions = useMemo(
        () => Array.from(new Set(certSource.map((i) => normalizeText(i.sds_name)).filter(Boolean))),
        [certSource],
    );
    const productOptions = useMemo(
        () => Array.from(new Set(certSource.map((i) => normalizeText(i.product)).filter(Boolean))),
        [certSource],
    );
    const regDocOptions = useMemo(
        () => Array.from(new Set(certSource.map((i) => normalizeText(i.regulatory_document)).filter(Boolean))),
        [certSource],
    );
    const validityOptions = useMemo(
        () => Array.from(new Set(certSource.map((i) => normalizeText(i.certificate_validity_period)).filter(Boolean))),
        [certSource],
    );
    const aptsCommentOptions = useMemo(
        () => Array.from(new Set(certSource.map((i) => normalizeText(i.apts_comment)).filter(Boolean))),
        [certSource],
    );
    const osRalStatusOptions = useMemo(
        () => Array.from(new Set(certSource.map((i) => normalizeText(i.os_ral_status)).filter(Boolean))),
        [certSource],
    );
    const labRalStatusOptions = useMemo(
        () => Array.from(new Set(certSource.map((i) => normalizeText(i.laboratory_ral_status)).filter(Boolean))),
        [certSource],
    );

    const manufacturerPickerOptions = useMemo<EntityPickerOption[]>(
        () => manufacturers.map((item) => ({
            id: item.id,
            label: buildManufacturerLabel(item),
            sublabel: item.inn ? `ИНН / УНП: ${item.inn}` : undefined,
            searchText: `${item.name} ${item.inn}`.trim(),
        })),
        [manufacturers],
    );

    const laboratoryPickerOptions = useMemo<EntityPickerOption[]>(
        () => laboratories.map((item) => ({
            id: item.id,
            label: buildLaboratoryLabel(item),
            sublabel: [item.number, item.status].filter(Boolean).join(' · ') || undefined,
            searchText: `${item.name} ${item.number} ${item.status}`.trim(),
        })),
        [laboratories],
    );

    const osPickerOptions = useMemo<EntityPickerOption[]>(
        () => osBodies.map((item) => ({
            id: item.id,
            label: buildOsLabel(item),
            sublabel: [item.code, item.status].filter(Boolean).join(' · ') || undefined,
            searchText: `${item.name} ${item.code} ${item.status}`.trim(),
        })),
        [osBodies],
    );

    const setField = (name: keyof CertificateFormState) => (value: string) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleEntityCreated = (entityType: VoluntaryEntityType, data: Record<string, unknown>) => {
        if (entityType === 'manufacturer') {
            const item: Manufacturer = {
                id: Number(data.id),
                name: String(data.manufacturer || ''),
                inn: String(data.inn_unp || ''),
            };
            setManufacturers((prev) => [...prev.filter((m) => m.id !== item.id), item]);
            setForm((prev) => ({ ...prev, manufacturer: buildManufacturerLabel(item) }));
            setSelectedManufacturerId(item.id);
        } else if (entityType === 'laboratory') {
            const item: Laboratory = {
                id: Number(data.id),
                name: String(data.laboratory_name || ''),
                number: String(data.accreditation_certificate || ''),
                status: String(data.laboratory_ral_status || ''),
            };
            setLaboratories((prev) => [...prev.filter((l) => l.id !== item.id), item]);
            setForm((prev) => ({ ...prev, laboratory: buildLaboratoryLabel(item) }));
            setSelectedLaboratoryId(item.id);
        } else {
            const item: OsBody = {
                id: Number(data.id),
                name: String(data.os_name || ''),
                code: String(data.os_code || ''),
                status: String(data.os_ral_status || ''),
            };
            setOsBodies((prev) => [...prev.filter((o) => o.id !== item.id), item]);
            setForm((prev) => ({ ...prev, os: buildOsLabel(item) }));
            setSelectedOsId(item.id);
        }
        setEntityModal(null);
    };

    const validateEntitySelection = () => {
        if (form.manufacturer && !selectedManufacturerId) {
            setError('Выберите производителя из списка или нажмите «Создать»');
            return false;
        }
        if (form.laboratory && !selectedLaboratoryId) {
            setError('Выберите лабораторию из списка или нажмите «Создать»');
            return false;
        }
        if (form.os && !selectedOsId) {
            setError('Выберите орган по сертификации из списка или нажмите «Создать»');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateEntitySelection()) return;

        setSubmitting(true);
        setError(null);

        try {
            const payload = compactPayload({
                apts_comment: form.apts_comment,
                certificate_link: form.certificate_link,
                certificate_number: form.certificate_number,
                sds_name: form.sds_name,
                form_number: form.blank_number,
                certificate_registration_date: form.certificate_registration_date,
                certificate_expiry_date: form.certificate_expiry_date,
                regulatory_document: form.regulatory_document,
                product: form.product,
                comment: form.comment,
                appeal_number: form.number_appeal,
                manufacture_id: selectedManufacturerId ?? undefined,
                laboratory_id: selectedLaboratoryId ?? undefined,
                organ_os_id: selectedOsId ?? undefined,
            });
            payload.certificate_validity_period = form.certificate_validity_period;

            if (isEdit && certificateId) {
                await axios.patch(getVoluntaryCertificateItemUrl(certificateId), payload, {
                    headers: authHeaders(),
                });
                setSuccessMessage('Сертификат обновлён');
                setTimeout(() => navigate(`/voluntary-certificates/${encodeURIComponent(form.certificate_number)}`), 1200);
            } else {
                await axios.post(getApiV1Url('VOLUNTARY_CERTIFICATES_CREATE'), payload, {
                    headers: authHeaders(),
                });
                setSuccessMessage('Сертификат сохранён');
                setForm(emptyForm());
                setSelectedManufacturerId(null);
                setSelectedLaboratoryId(null);
                setSelectedOsId(null);
                setTimeout(() => setSuccessMessage(null), 2500);
            }
        } catch (err: any) {
            const serverData = err?.response?.data;
            setError(serverData ? JSON.stringify(serverData) : (err?.message || 'Ошибка сохранения'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="page-container">
                <p>Загрузка данных сертификата...</p>
            </div>
        );
    }

    return (
        <div className="page-container">
            <nav className="breadcrumbs">
                <span
                    className="breadcrumb-home"
                    style={{ color: 'rgba(0,0,0,0.5)', cursor: 'pointer', fontWeight: 400 }}
                    onClick={() => navigate('/')}
                >
                    Главная
                </span>
                <span style={{ margin: '0 8px', color: 'rgba(0,0,0,0.3)' }}>/</span>
                <span
                    style={{ color: 'rgba(0,0,0,0.5)', cursor: 'pointer', fontWeight: 400 }}
                    onClick={() => navigate('/voluntary-certificates')}
                >
                    Добровольные сертификаты
                </span>
                <span style={{ margin: '0 8px', color: 'rgba(0,0,0,0.3)' }}>/</span>
                <span className="breadcrumb-current" style={{ fontWeight: 500, color: '#000' }}>
                    {isEdit ? 'Редактирование сертификата' : 'Добавление сертификата'}
                </span>
            </nav>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <div className="form-container">
                <h2>{isEdit ? 'Редактировать сертификат' : 'Добавить сертификат'}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Комментарий АПТС</label>
                        <input
                            list="apts-comment-list"
                            value={form.apts_comment}
                            onChange={(e) => setField('apts_comment')(e.target.value)}
                        />
                        <datalist id="apts-comment-list">
                            {aptsCommentOptions.map((opt, i) => (<option key={i} value={opt} />))}
                        </datalist>
                    </div>
                    <div className="form-group">
                        <label>Ссылка на сертификат</label>
                        <input value={form.certificate_link} onChange={(e) => setField('certificate_link')(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>№ сертификата</label>
                        <input value={form.certificate_number} onChange={(e) => setField('certificate_number')(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Срок действия сертификата</label>
                        <input
                            list="validity-list"
                            value={form.certificate_validity_period}
                            onChange={(e) => setField('certificate_validity_period')(e.target.value)}
                            placeholder="Оставьте пустым для автоматического статуса по дате окончания"
                        />
                        <datalist id="validity-list">
                            {validityOptions.map((opt, i) => (<option key={i} value={opt} />))}
                        </datalist>
                    </div>
                    <div className="form-group">
                        <label>Наименование СДС</label>
                        <input
                            list="sds-list"
                            value={form.sds_name}
                            onChange={(e) => setField('sds_name')(e.target.value)}
                        />
                        <datalist id="sds-list">
                            {sdsOptions.map((opt, i) => (<option key={i} value={opt} />))}
                        </datalist>
                    </div>
                    <div className="form-group">
                        <label>№ бланка</label>
                        <input value={form.blank_number} onChange={(e) => setField('blank_number')(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Дата регистрации сертификата</label>
                        <input
                            type="date"
                            value={form.certificate_registration_date}
                            onChange={(e) => setField('certificate_registration_date')(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Дата окончания действия сертификата</label>
                        <input
                            type="date"
                            value={form.certificate_expiry_date}
                            onChange={(e) => setField('certificate_expiry_date')(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Нормативный документ</label>
                        <input
                            list="regdoc-list"
                            value={form.regulatory_document}
                            onChange={(e) => setField('regulatory_document')(e.target.value)}
                        />
                        <datalist id="regdoc-list">
                            {regDocOptions.map((opt, i) => (<option key={i} value={opt} />))}
                        </datalist>
                    </div>
                    <div className="form-group">
                        <label>Продукция</label>
                        <input
                            list="product-list"
                            value={form.product}
                            onChange={(e) => setField('product')(e.target.value)}
                        />
                        <datalist id="product-list">
                            {productOptions.map((opt, i) => (<option key={i} value={opt} />))}
                        </datalist>
                    </div>
                    <div className="form-group">
                        <label>Комментарий</label>
                        <input value={form.comment} onChange={(e) => setField('comment')(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Номер обращения</label>
                        <input value={form.number_appeal} onChange={(e) => setField('number_appeal')(e.target.value)} />
                    </div>

                    <div className="form-group">
                        <label>Производитель</label>
                        <EntityPickerInput
                            id="manufacturer"
                            value={form.manufacturer}
                            options={manufacturerPickerOptions}
                            placeholder="Начните вводить название или ИНН"
                            createLabel="+ Создать производителя"
                            onChange={(value) => {
                                setField('manufacturer')(value);
                                setSelectedManufacturerId(null);
                            }}
                            onSelect={(option) => {
                                setField('manufacturer')(option.label);
                                setSelectedManufacturerId(option.id);
                            }}
                            onCreateClick={(prefill) => setEntityModal({ type: 'manufacturer', prefill })}
                        />
                        {selectedManufacturerId && (
                            <small style={{ color: '#2f6f2f', fontSize: 12 }}>Выбран производитель из справочника</small>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Лаборатория</label>
                        <EntityPickerInput
                            id="laboratory"
                            value={form.laboratory}
                            options={laboratoryPickerOptions}
                            placeholder="Начните вводить название лаборатории"
                            createLabel="+ Создать лабораторию"
                            onChange={(value) => {
                                setField('laboratory')(value);
                                setSelectedLaboratoryId(null);
                            }}
                            onSelect={(option) => {
                                setField('laboratory')(option.label);
                                setSelectedLaboratoryId(option.id);
                            }}
                            onCreateClick={(prefill) => setEntityModal({ type: 'laboratory', prefill })}
                        />
                        {selectedLaboratoryId && (
                            <small style={{ color: '#2f6f2f', fontSize: 12 }}>Выбрана лаборатория из справочника</small>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Орган по сертификации</label>
                        <EntityPickerInput
                            id="os"
                            value={form.os}
                            options={osPickerOptions}
                            placeholder="Начните вводить название ОС"
                            createLabel="+ Создать орган по сертификации"
                            onChange={(value) => {
                                setField('os')(value);
                                setSelectedOsId(null);
                            }}
                            onSelect={(option) => {
                                setField('os')(option.label);
                                setSelectedOsId(option.id);
                            }}
                            onCreateClick={(prefill) => setEntityModal({ type: 'os', prefill })}
                        />
                        {selectedOsId && (
                            <small style={{ color: '#2f6f2f', fontSize: 12 }}>Выбран орган из справочника</small>
                        )}
                    </div>

                    <button type="submit" className="submit-button" disabled={submitting}>
                        {submitting ? 'Сохранение...' : (isEdit ? 'Сохранить изменения' : 'Сохранить сертификат')}
                    </button>
                </form>
            </div>

            <VoluntaryEntityCreateModal
                isOpen={Boolean(entityModal)}
                entityType={entityModal?.type || 'manufacturer'}
                prefillName={entityModal?.prefill || ''}
                osRalStatusOptions={osRalStatusOptions}
                labRalStatusOptions={labRalStatusOptions}
                onClose={() => setEntityModal(null)}
                onCreated={handleEntityCreated}
            />
        </div>
    );
}
